import { useState } from "react";
import { Link } from "react-router-dom";
import type { SpendingReport } from "../../entities/ledger/types";
import { money } from "../../shared/lib/format";
import { EmptyState } from "../../shared/ui/EmptyState";

const VISIBLE_CATEGORIES = 10;

// A figure is only trustworthy if the reader can reach the rows behind it, so
// every category links to the journal filtered to exactly that category over
// exactly the period the figure was computed for.
function journalLink(categoryID: string | undefined, period?: { from: string; to: string }): string {
  const query = new URLSearchParams({ category: categoryID ?? "none" });
  if (period) { query.set("from", period.from); query.set("to", period.to); }
  return `/journal?${query}`;
}

// "Where was money spent?" — derived by the server from postings on
// expense/income accounts grouped by the transaction's category. Transfers,
// openings and reconciliations never appear here.
interface SpendingByCategoryProps {
  report: SpendingReport;
  // The period the figures were computed over, so a row can hand the same
  // window to the journal instead of silently switching to the default.
  period?: { from: string; to: string };
}

export function SpendingByCategory({ report, period }: SpendingByCategoryProps) {
  const [expanded, setExpanded] = useState(false);
  const allRows = report.byCategory.filter((group) => group.expenses.base.minor !== 0 || group.income.base.minor !== 0);
  const rows = expanded ? allRows : allRows.slice(0, VISIBLE_CATEGORIES);
  const max = Math.max(1, ...allRows.map((group) => Math.abs(group.expenses.base.minor)));
  const showReporting = report.reportingCurrency !== report.baseCurrency;
  return (
    <section className="panel category-panel" aria-labelledby="category-title">
      <div className="panel-heading">
        <div><p className="eyebrow">This month</p><h2 id="category-title">Where the money went</h2></div>
        <div className="category-totals">
          <span><small>Expenses</small><strong>{money(report.expenses.base.minor, report.baseCurrency)}</strong>{showReporting && report.expenses.reporting ? <em>{money(report.expenses.reporting.minor, report.reportingCurrency)}</em> : null}</span>
          <span><small>Income</small><strong>{money(report.income.base.minor, report.baseCurrency)}</strong>{showReporting && report.income.reporting ? <em>{money(report.income.reporting.minor, report.reportingCurrency)}</em> : null}</span>
        </div>
      </div>
      {allRows.length === 0 ? <EmptyState>Nothing in this period carries a category yet. Categorise an operation in the journal and it appears here.</EmptyState> : null}
      <ul className="category-list">
        {rows.map((group) => (
          <li key={group.categoryId ?? "uncategorized"}>
            <Link className="category-row" to={journalLink(group.categoryId, period)}
              title={`Show the ${group.count} ${group.count === 1 ? "transaction" : "transactions"} behind ${group.name}`}>
              <span className="category-name">{group.name}<small>{group.count} {group.count === 1 ? "operation" : "operations"}</small></span>
              <span className="category-amount">
                {group.expenses.base.minor !== 0 ? <strong>{money(group.expenses.base.minor, report.baseCurrency)}</strong> : <strong className="income-amount">+{money(group.income.base.minor, report.baseCurrency)}</strong>}
                {showReporting ? <small>{group.expenses.base.minor !== 0
                  ? (group.expenses.reporting ? money(group.expenses.reporting.minor, report.reportingCurrency) : "rate unavailable")
                  : (group.income.reporting ? money(group.income.reporting.minor, report.reportingCurrency) : "rate unavailable")}</small> : null}
              </span>
            </Link>
            <div className="category-bar"><span style={{ width: `${Math.round((Math.abs(group.expenses.base.minor) / max) * 100)}%` }} /></div>
          </li>
        ))}
      </ul>
      {allRows.length > VISIBLE_CATEGORIES ? (
        <div className="pager">
          <span>{rows.length} of {allRows.length} categories</span>
          <button className="chip" type="button" onClick={() => setExpanded((value) => !value)}>{expanded ? "Show top 10" : `Show all ${allRows.length}`}</button>
        </div>
      ) : null}
      {!report.expenses.reportingComplete || !report.income.reportingComplete ? (
        <p className="panel-note">Some operations have no exchange rate for their day yet, so the {report.reportingCurrency} totals are partial.</p>
      ) : null}
    </section>
  );
}
